// @ts-strict-ignore
import mapValues from 'lodash/mapValues';
import {immerable} from 'immer';

import {
  MinimalProjectMetadata as MinimalProjectApiResponse,
  ProjectMetadata as ProjectApiResponse,
} from 'app_api/projects/types';
import {currentProjectClient} from 'common/report/queries/app-api/project-details-client';
import {extendWorkspace, toWorkspaceObj, WorkspaceObj} from 'common/report/queries/app-api/workspaces-client';
import {getFeatureFlagUrlOverrides} from 'common/util/url';
import {Timezone} from 'common/constants';

import {
  FeatureGate,
  FeatureGatesValue,
  INTERNAL_PROJECTS,
  ProjectFeatureFlag,
  ProjectType,
  RestrictedReport,
  RestrictEntitySaving,
} from '../constants';
import {Store} from './store';
import {ExperimentSettings, TimePeriodSettings} from './types';
import {Blocking, ExternalHelpLink, Role} from './types';

export class ProjectDetails {
  [immerable] = true;

  // TODO tgd 2021-03-08 callers temporarily need to account for `blocking`
  // being undefined, when it comes from a cached value. After a few weeks,
  // clean up all references: `blocking?.foo` -> `blocking.foo`
  public declare blocking: Blocking;
  public declare domain: string;
  public declare eventsPlanID: number;
  public declare eventsPlanName: string;
  public declare externalHelpLinks: ExternalHelpLink[];
  public declare hasIntegratedArb: boolean;
  public declare hasIntegratedEngage: boolean;
  public declare hasPublicDashboardsEnabled: boolean;
  public declare hasSensitiveAccess: boolean;
  public declare hasWorkspaces: boolean;
  public declare id: number;
  public declare isDemo: boolean;
  public declare name: string;
  public declare onboardingDashboardID: number;
  public declare organizationId: number;
  public declare ownerID: number;
  public declare peoplePlanID: number;
  public declare projectAsOfDate: null | string;
  public declare queryTimezone: string;
  public declare queryTimezoneUtcOffset: number;
  public declare role: Role;
  public declare timezone: Timezone;
  public declare type: ProjectType;
  public declare utcOffset: number;
  public declare workspaces: {[key: string]: WorkspaceObj};
  public declare hasEventApprovalEnabled: boolean;
  public declare showImplementationDeadline: boolean;
  public declare implementationDeadline: string;
  public declare isFree: boolean;
  public declare isSelfServe: boolean;
  public declare isEnterprise: boolean;

  public declare timePeriodSettings: TimePeriodSettings;
  public declare experimentSettings: ExperimentSettings;

  // these should be accessed through helpers hasFeatureFlag() etc
  private declare featureFlags: Set<string>;
  private declare permissions: Set<string>;
  private declare featureGates: FeatureGatesValue;
  private declare restrictedReports: Set<string>;
  private declare restrictedEntitySaving: Set<string>;

  static MINIMAL_COPYABLE_PROPS: {[key: string]: keyof MinimalProjectApiResponse} = {
    domain: `domain`,
    hasWorkspaces: `has_workspaces`,
    id: `id`,
    isDemo: `is_demo`,
    name: `name`,
    organizationId: `organization_id`,
    type: `type`,
  };
  static OTHER_COPYABLE_PROPS: {[key: string]: keyof ProjectApiResponse} = {
    eventsPlanID: `events_plan_id`,
    eventsPlanName: `events_plan_name`,
    hasIntegratedArb: `has_integrated_arb`,
    hasIntegratedEngage: `has_integrated_engage`,
    hasIntegratedSessionReplay: `has_integrated_session_replay`,
    hasSensitiveAccess: `hasSensitiveAccess`,
    onboardingDashboardID: `onboarding_dashboard_id`,
    ownerID: `owner_id`,
    peoplePlanID: `people_plan_id`,
    timezone: `timezone`,
    utcOffset: `utc_offset`,
  };
  static ALL_COPYABLE_PROPS: {[key: string]: keyof ProjectApiResponse} = {
    ...ProjectDetails.MINIMAL_COPYABLE_PROPS,
    ...ProjectDetails.OTHER_COPYABLE_PROPS,
  };

  constructor(projectAttrs: MinimalProjectApiResponse, existingProject?: ProjectDetails);
  constructor(projectAttrs: ProjectApiResponse, existingProject?: ProjectDetails) {
    if (existingProject) {
      for (const prop of Object.keys(ProjectDetails.ALL_COPYABLE_PROPS)) {
        this[prop] = existingProject[prop];
      }
      this.externalHelpLinks = [...existingProject.externalHelpLinks];
      this.featureFlags = new Set(existingProject.featureFlags);
      this.projectAsOfDate = existingProject.projectAsOfDate;
      this.permissions = new Set(existingProject.permissions);
      this.role = {...existingProject.role};
      this.workspaces = {...existingProject.workspaces};
      this.blocking = existingProject.blocking;
      this.featureGates = existingProject.featureGates;
      this.restrictedReports = existingProject.restrictedReports;
      this.restrictedEntitySaving = existingProject.restrictedEntitySaving;
    } else {
      // defaults
      this.blocking = {
        isBlockedEvents: false,
        isBlockedPeople: false,
        blockedReplayWindows: [],
        label: ``,
      };
      this.externalHelpLinks = [];
      this.featureFlags = new Set();
      this.restrictedReports = new Set();
      this.restrictedEntitySaving = new Set();
      // the app API uses null as the default value to indicate the lack of a project as-of date, but note that
      // you might want to coalesce this to an empty string before passing into attributes because panel attributes cast null to "null"
      this.projectAsOfDate = null;
      this.permissions = new Set();
      this.role = {};
      this.workspaces = {};
      this.featureGates = null;
    }

    this.timePeriodSettings = {
      weekStartDay: projectAttrs.time_period_settings?.week_start_day ?? 0,
      quarterStartMonth: projectAttrs.time_period_settings?.quarter_start_month ?? 0,
    };
    this.experimentSettings = {
      eventName: projectAttrs.experiment_settings?.event_name,
      nameProperty: projectAttrs.experiment_settings?.name_property,
      variantProperty: projectAttrs.experiment_settings?.variant_property,
    };

    // test for minimal vs full project details
    if (projectAttrs.external_help_link) {
      for (const [prop, apiName] of Object.entries(ProjectDetails.ALL_COPYABLE_PROPS)) {
        this[prop] = projectAttrs[apiName];
      }
      this.blocking = {
        isBlockedEvents: projectAttrs.blocking.is_blocked_events,
        isBlockedPeople: projectAttrs.blocking.is_blocked_people,
        blockedReplayWindows: projectAttrs.blocking.blocked_replay_windows,
        label: projectAttrs.blocking.label,
      };
      this.externalHelpLinks = projectAttrs.external_help_link.map((link) => ({
        linkName: link.link_name,
        linkURL: link.link_url,
      }));
      this.featureFlags = new Set(projectAttrs.feature_flags);
      this.projectAsOfDate = projectAttrs.project_as_of_date;
      this.hasPublicDashboardsEnabled = projectAttrs.has_public_dashboards_enabled;
      this.hasEventApprovalEnabled = projectAttrs.has_event_approval_enabled;
      this.permissions = new Set(projectAttrs.permissions);
      this.featureGates = projectAttrs.feature_gates as FeatureGatesValue;
      this.restrictedReports = new Set(this.featureGates?.[FeatureGate.RestrictedReports]);
      this.restrictedEntitySaving = new Set(this.featureGates?.[FeatureGate.RestrictEntitySaving]);
      this.role = projectAttrs.role || {};
      this.workspaces = mapValues(projectAttrs.workspaces, toWorkspaceObj);
      this.showImplementationDeadline = projectAttrs.show_implementation_deadline;
      this.implementationDeadline = projectAttrs.implementation_deadline;
      this.isFree = projectAttrs.is_free;
      this.isSelfServe = projectAttrs.is_selfserve;
      this.isEnterprise = projectAttrs.is_enterprise;
    } else {
      for (const [prop, apiName] of Object.entries(ProjectDetails.MINIMAL_COPYABLE_PROPS)) {
        this[prop] = projectAttrs[apiName];
      }
      this.workspaces = mapValues(projectAttrs.workspaces, (minimalWorkspace) =>
        extendWorkspace(this.workspaces[minimalWorkspace.id] || {}, minimalWorkspace),
      );
    }
  }

  private isInternal() {
    return INTERNAL_PROJECTS.has(this.id);
  }

  public hasFeatureFlag(flagName: ProjectFeatureFlag, enableInDogfoodProjects = false) {
    const featureFlagOverrides = getFeatureFlagUrlOverrides();
    if (Object.prototype.hasOwnProperty.call(featureFlagOverrides, flagName)) {
      return featureFlagOverrides[flagName];
    }
    return (
      this.featureFlags.has(flagName) || (enableInDogfoodProjects && this.isInternal()) || this.featureFlags.has(`dev`)
    );
  }

  public getFeatureGate<FeatureGateName extends keyof FeatureGatesValue>(
    gate: FeatureGateName,
  ): FeatureGatesValue[FeatureGateName] | null {
    return this.featureGates?.[gate];
  }

  public hasPermission(permissionName: string) {
    return this.permissions.has(permissionName);
  }

  public updateWorkspaces(workspaces: {[key: string]: WorkspaceObj}) {
    Object.assign(this.workspaces, workspaces);
  }

  public isRollupProject(): boolean {
    return this.type === ProjectType.Rollup;
  }

  public hasAccessToReport(name: RestrictedReport) {
    return !this.restrictedReports.has(name);
  }

  public canSaveEntity(name: RestrictEntitySaving) {
    return !this.restrictedEntitySaving.has(name);
  }

  public isPeopleOnlyProject() {
    return (
      (this.blocking.isBlockedEvents || !this.hasIntegratedArb) &&
      this.hasIntegratedEngage &&
      !this.blocking.isBlockedPeople
    );
  }

  public canUseCohorts(): boolean {
    return this.hasAccessToReport(RestrictedReport.Cohorts) || this.hasFeatureFlag(ProjectFeatureFlag.Cohorts);
  }

  public canUseDataGroups(): boolean {
    return this.hasFeatureFlag(ProjectFeatureFlag.B2BAnalytics) || !!this.getFeatureGate(FeatureGate.B2BAnalytics);
  }

  public canUseVerifiedContent(): boolean {
    return this.getFeatureGate(FeatureGate.VerifiedContent) || this.hasFeatureFlag(ProjectFeatureFlag.VerifiedContent);
  }
}

/**
 * @deprecated
 * Use currentProject Model Context instead:
 * `getContext('currentProject').get()`
 */
export const currentProjectStore = new Store<ProjectDetails, ProjectApiResponse>({
  apiClient: currentProjectClient,
  defaultValue: null,
  name: `current-project`,
  transform: {
    index: (project: ProjectApiResponse) => new ProjectDetails(project),
  },
});

export type CurrentProjectStore = typeof currentProjectStore;
